export const SNAP_GRID_TYPE_CENTER_CROSS = 'center-cross';
export const SNAP_GRID_TYPE_THIRDS = 'thirds';
export const SNAP_GRID_TYPE_LOWER_THIRD = 'lower-third';
export const DEFAULT_SNAP_GRID_TYPE = SNAP_GRID_TYPE_CENTER_CROSS;
export const SNAP_THRESHOLD = 12;

const SNAP_GRID_TYPE_SET = new Set([
  SNAP_GRID_TYPE_CENTER_CROSS,
  SNAP_GRID_TYPE_THIRDS,
  SNAP_GRID_TYPE_LOWER_THIRD,
]);

const clamp = (value, min, max) => Math.min(Math.max(value, min), max);

const uniqueSorted = (values) => [...new Set(values.map((value) => Number(value).toFixed(3)))].map(Number).sort((a, b) => a - b);

const normalizeSnapGridType = (gridType) => (
  SNAP_GRID_TYPE_SET.has(gridType) ? gridType : DEFAULT_SNAP_GRID_TYPE
);

const getGridFractions = (gridType, axis) => {
  const normalized = normalizeSnapGridType(gridType);
  if (normalized === SNAP_GRID_TYPE_THIRDS) return [1 / 3, 2 / 3];
  if (normalized === SNAP_GRID_TYPE_LOWER_THIRD) {
    // Lower-third framing guides biased toward the edges.
    if (axis === 'x') return [1 / 5, 4 / 5];
    // Typical lower-third placement guide closer to the bottom.
    return [4 / 5];
  }
  return [1 / 2];
};

const buildGridAxisLines = (size, gridType, axis) => {
  const safeSize = Math.max(0, Number(size) || 0);
  const fractions = getGridFractions(gridType, axis);
  return uniqueSorted(fractions.map((fraction) => safeSize * fraction));
};

export const buildCanvasSnapLines = ({
  width,
  height,
  gridType = DEFAULT_SNAP_GRID_TYPE,
}) => {
  const safeWidth = Math.max(0, Number(width) || 0);
  const safeHeight = Math.max(0, Number(height) || 0);
  const xLines = [0, safeWidth, ...buildGridAxisLines(safeWidth, gridType, 'x')];
  const yLines = [0, safeHeight, ...buildGridAxisLines(safeHeight, gridType, 'y')];

  return {
    x: uniqueSorted(xLines),
    y: uniqueSorted(yLines),
  };
};

export const buildCanvasVisibleGridLines = ({
  width,
  height,
  gridType = DEFAULT_SNAP_GRID_TYPE,
}) => ({
  x: buildGridAxisLines(width, gridType, 'x'),
  y: buildGridAxisLines(height, gridType, 'y'),
});

const boundsCenter = (bounds) => ({
  x: bounds.minX + bounds.width / 2,
  y: bounds.minY + bounds.height / 2,
});

const offsetBounds = (bounds, delta) => ({
  minX: bounds.minX + delta.x,
  minY: bounds.minY + delta.y,
  maxX: bounds.maxX + delta.x,
  maxY: bounds.maxY + delta.y,
  width: bounds.width,
  height: bounds.height,
});

const findAxisSnap = ({ anchors, lines, threshold }) => {
  let best = null;
  for (const anchor of anchors) {
    for (const line of lines) {
      const correction = line - anchor.value;
      const distance = Math.abs(correction);
      if (distance > threshold) continue;
      if (!best || distance < best.distance) {
        best = {
          correction,
          distance,
          line,
          anchorKey: anchor.key,
        };
      }
    }
  }
  return best;
};

export const snapTranslatedBounds = ({
  startBounds,
  delta,
  canvasWidth,
  canvasHeight,
  gridType = DEFAULT_SNAP_GRID_TYPE,
  threshold = SNAP_THRESHOLD,
}) => {
  if (!startBounds) {
    return {
      delta: { x: Number(delta?.x) || 0, y: Number(delta?.y) || 0 },
      guides: { vertical: [], horizontal: [] },
      snappedX: false,
      snappedY: false,
    };
  }

  const baseDelta = {
    x: Number(delta?.x) || 0,
    y: Number(delta?.y) || 0,
  };
  const numericThreshold = Number(threshold);
  const safeThreshold = Math.max(0, Number.isFinite(numericThreshold) ? numericThreshold : SNAP_THRESHOLD);
  const snapLines = buildCanvasSnapLines({ width: canvasWidth, height: canvasHeight, gridType });
  const movedBounds = offsetBounds(startBounds, baseDelta);
  const movedCenter = boundsCenter(movedBounds);

  const xSnap = findAxisSnap({
    anchors: [
      { key: 'minX', value: movedBounds.minX },
      { key: 'centerX', value: movedCenter.x },
      { key: 'maxX', value: movedBounds.maxX },
    ],
    lines: snapLines.x,
    threshold: safeThreshold,
  });

  const ySnap = findAxisSnap({
    anchors: [
      { key: 'minY', value: movedBounds.minY },
      { key: 'centerY', value: movedCenter.y },
      { key: 'maxY', value: movedBounds.maxY },
    ],
    lines: snapLines.y,
    threshold: safeThreshold,
  });

  const snappedDelta = {
    x: xSnap ? baseDelta.x + xSnap.correction : baseDelta.x,
    y: ySnap ? baseDelta.y + ySnap.correction : baseDelta.y,
  };

  const guides = {
    vertical: xSnap ? [clamp(xSnap.line, 0, Math.max(0, Number(canvasWidth) || 0))] : [],
    horizontal: ySnap ? [clamp(ySnap.line, 0, Math.max(0, Number(canvasHeight) || 0))] : [],
  };

  return {
    delta: snappedDelta,
    guides,
    snappedX: Boolean(xSnap),
    snappedY: Boolean(ySnap),
    snapMeta: {
      x: xSnap,
      y: ySnap,
    },
  };
};
